import { useCallback, useState } from 'react';
import { Outlet, useChildMatches, useNavigate, useParams } from '@tanstack/react-router';
import type { CardDetail as CardDetailEntity, CardId } from '@voll-intelligence/domain';

import { env } from '@web/core/env';
import { t } from '@web/i18n';
import {
  EmptyHint,
  TabBar,
  type CardTripState,
  useCardDetail,
  useCardTrip,
  useEscapeKey,
} from '@web/ui/core';
import { type AppIconName, AppIcons, Avatar, AvatarFallback, IconButton } from '@web/ui/core/atoms';
import { ChannelChip, CustomerAvatar, ToneChip } from '@web/ui/core/molecules';
import { fireAndForget, agentKindIcon, twcn } from '@web/ui/core/utils';

import { CardActionsRow } from './components/card_actions_row';
import { CardSideColumn } from './components/card_side_column';
import { type CardDetailTabId, type CardRoutes, useCardRoutes } from './hooks/use_card_routes';
import { ConversationView } from './views/conversation_view';
import { HistoryView } from './views/history_view';
import { IdentityView } from './views/identity_view';
import { TasksView } from './views/tasks_view';

const TABS: readonly CardDetailTabId[] = ['conversation', 'tasks', 'identity', 'history'];

const TAB_LABEL: Record<CardDetailTabId, Parameters<typeof t>[0]> = {
  tasks: 'board.drawer.tab.tasks',
  conversation: 'board.drawer.tab.conversation',
  identity: 'board.drawer.tab.identity',
  history: 'board.drawer.tab.history',
};

const TAB_ICON: Record<CardDetailTabId, AppIconName> = {
  tasks: 'list-checks',
  conversation: 'chat-circle',
  identity: 'identity',
  history: 'clock-countdown',
};

/**
 * URL-driven card detail (route target + master-detail layout). Owns routing,
 * tab selection and the header (via `useCardDetail`); each tab view under
 * `views/` resolves its own data, loading and error states through its own
 * hooks.
 *
 * It renders the shell (`CardDetailShell`, em um dos três modos — ver
 * `shellMode`) directly so the task detail
 * child route can sit beside the card content: when a task child is active the
 * `<Outlet />` left column shows the task panel (splitting the width);
 * otherwise that column is hidden.
 *
 * O card assumido pelo próprio operador (`assignedToMe`) abre em tela cheia, e
 * só ele ganha a coluna da viagem ao lado da conversa — recolhível pelo header
 * (persistido) a partir de xl, em Drawer abaixo disso.
 */
export function CardDetail(): React.JSX.Element {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const routes = useCardRoutes();
  const childMatches = useChildMatches();

  const cardId = params.cardId as CardId | undefined;
  const tab: CardDetailTabId = isCardDetailTab(params.tab) ? params.tab : 'conversation';

  // The task detail route is the only child of this route; it renders its own
  // docked aside (see `TaskDetail`) and carries the open task id we highlight.
  const taskChild = childMatches[0];
  const activeTaskId = (taskChild?.params as { taskId?: string } | undefined)?.taskId;

  const goBack = useCallback((): void => {
    fireAndForget(navigate(routes.back));
  }, [navigate, routes.back]);

  useEscapeKey(goBack);

  const goAfterTransfer = useCallback((): void => {
    fireAndForget(navigate(routes.afterTransfer));
  }, [navigate, routes.afterTransfer]);

  const detailQuery = useCardDetail(cardId);
  const detail = detailQuery.data;
  const fullscreen = detail?.assignedToMe === true;
  // Só o card assumido vira tela cheia; no drawer (card da IA ou de outro
  // operador) nem pedimos a viagem — `useCardTrip(undefined)` devolve 'none'
  // sem disparar leitura nenhuma.
  const tripState = useCardTrip(fullscreen ? cardId : undefined);
  // Preferência de SESSÃO e POR CARD, não de layout: toda abertura de card
  // começa com a coluna expandida (ela carrega o contato do cliente). Guardar o
  // id do card recolhido, e não um boolean, faz a troca A → B na mesma
  // instância (só os params mudam) reabrir a coluna sem effect nem reset.
  // Persistir escondia a coluna para quem recolheu a viagem antes.
  const [collapsedForCardId, setCollapsedForCardId] = useState<CardId | null>(null);
  const sideCollapsed = cardId !== undefined && collapsedForCardId === cardId;
  const toggleSideCollapsed = (): void => {
    setCollapsedForCardId(sideCollapsed ? null : (cardId ?? null));
  };
  const [sideDrawerOpen, setSideDrawerOpen] = useState(false);

  const showSide = shouldShowSideColumn(fullscreen, tab);

  // Sincroniza o estado local da gaveta com a condição derivada: quando a coluna
  // sai de cena (outra aba, card devolvido) a gaveta não pode ficar armada para
  // reabrir sozinha na próxima vez. Ajustado no render, sem um commit intermediário.
  if (!showSide && sideDrawerOpen) setSideDrawerOpen(false);

  if (!cardId) return <></>;

  return (
    <CardDetailShell mode={shellMode(fullscreen, routes.family)} onClose={goBack}>
      <div className="border-b border-border-soft">
        <div className="flex items-center gap-3 px-5 py-3">
          <IconButton
            icon="arrow-left"
            variant="ghost"
            size="sm"
            onClick={goBack}
            aria-label={t('workspace.drawer.closeAria')}
            data-testid="drawer-close"
          />
          {detail ? (
            <>
              <CardHeaderIdentity detail={detail} />
              <CardHeaderAgent detail={detail} />
              {showSide ? (
                <CardSideHeaderToggles
                  collapsed={sideCollapsed}
                  onToggle={toggleSideCollapsed}
                  onOpenDrawer={() => {
                    setSideDrawerOpen(true);
                  }}
                />
              ) : null}
            </>
          ) : (
            <CardHeaderIdentitySkeleton cardId={cardId} errored={detailQuery.isError} />
          )}
        </div>
      </div>

      <CardActionsRow cardId={cardId} detail={detail} onTransferred={goAfterTransfer} />

      <CardDetailTabs routes={routes} activeTab={tab} />

      <CardDetailBody
        tab={tab}
        cardId={cardId}
        activeTaskId={activeTaskId}
        tripState={tripState}
        withSideColumn={showSide && !sideCollapsed}
      />
      {showSide && sideDrawerOpen ? (
        <CardSideColumn
          cardId={cardId}
          tripState={tripState}
          variant="drawer"
          onCloseDrawer={() => {
            setSideDrawerOpen(false);
          }}
        />
      ) : null}
    </CardDetailShell>
  );
}

/**
 * A coluna entra junto com a tela cheia, na conversa; não depende da viagem —
 * sem viagem ela mostra só o bloco Cliente (ver `CardSideColumn`).
 */
function shouldShowSideColumn(fullscreen: boolean, tab: CardDetailTabId): boolean {
  return fullscreen && tab === 'conversation';
}

type CardDetailShellMode = 'fullscreen' | 'drawer' | 'inline';

/**
 * Qual das três molduras o card usa. Tela cheia (card assumido por mim) ganha a
 * tela inteira em qualquer família. Fora dela, a triagem é INLINE: o card mora
 * na célula do `<Outlet />` da `TriagePage`, com a lista da triagem ao lado —
 * uma gaveta `fixed` com scrim cobriria essa lista e travaria o operador no
 * primeiro card (a `TriagePage` reabre o primeiro card quando nenhum está
 * ativo). No board de squad não há lista ao lado: segue gaveta com scrim.
 */
function shellMode(fullscreen: boolean, family: CardRoutes['family']): CardDetailShellMode {
  if (fullscreen) return 'fullscreen';
  return family === 'triage' ? 'inline' : 'drawer';
}

/**
 * A moldura da tela, nos três modos:
 * - `drawer`: gaveta ancorada à direita, com scrim atrás (card da IA ou de
 *   outro operador no board do squad);
 * - `fullscreen`: o card assumido toma a tela inteira (sem scrim — não há nada
 *   por baixo para clicar);
 * - `inline`: o card de triagem não assumido preenche a célula do `<Outlet />`
 *   da triagem, sem `fixed` e sem scrim, para a lista ao lado continuar
 *   clicável. Inline não é modal, então o painel não é `role="dialog"` — mas
 *   mantém o `data-testid="drawer"` dos outros modos.
 *
 * Em todos eles o `<Outlet />` do detalhe da tarefa fica ao lado do painel.
 */
function CardDetailShell({
  mode,
  onClose,
  children,
}: Readonly<{
  mode: CardDetailShellMode;
  onClose: () => void;
  children: React.ReactNode;
}>): React.JSX.Element {
  const overlaySize = mode === 'fullscreen' ? 'min-w-0 flex-1' : 'w-drawer-w-wide shrink-0';
  const panelClassName = twcn(
    'flex flex-col overflow-hidden bg-surface',
    mode === 'inline'
      ? 'min-w-0 flex-1'
      : `border-l border-border-soft shadow-overlay ${overlaySize}`,
  );

  if (mode === 'inline')
    return (
      <div className="flex h-full min-h-0 flex-1">
        <Outlet />
        <section
          aria-label={t('board.drawer.tabsAria')}
          className={panelClassName}
          data-testid="drawer"
        >
          {children}
        </section>
      </div>
    );

  return (
    <>
      {mode === 'fullscreen' ? null : (
        <button
          type="button"
          aria-label={t('workspace.drawer.closeAria')}
          onClick={onClose}
          className="fixed inset-0 z-scrim bg-foreground/30 backdrop-blur-sm cursor-pointer animate-scrim-in"
          data-testid="drawer-backdrop"
        />
      )}
      <div
        className={
          mode === 'fullscreen'
            ? 'fixed inset-0 z-drawer flex'
            : 'fixed top-0 right-0 bottom-0 z-drawer flex animate-drawer-in'
        }
      >
        <Outlet />
        <div role="dialog" aria-modal="true" className={panelClassName} data-testid="drawer">
          {children}
        </div>
      </div>
    </>
  );
}

/**
 * Os dois botões `sidebar-simple` do header em tela cheia: em ≥ xl alternam a
 * coluna lateral; abaixo disso abrem o Drawer. São dois porque `IconButton`
 * não aceita `className` — a visibilidade responsiva mora no `<span>` em volta.
 */
function CardSideHeaderToggles({
  collapsed,
  onToggle,
  onOpenDrawer,
}: Readonly<{
  collapsed: boolean;
  onToggle: () => void;
  onOpenDrawer: () => void;
}>): React.JSX.Element {
  return (
    <>
      <span className="hidden xl:inline-flex">
        <IconButton
          icon="sidebar-simple"
          variant="ghost"
          size="sm"
          onClick={onToggle}
          aria-label={t(collapsed ? 'card.side.expand' : 'card.side.collapse')}
          data-testid="side-column-toggle"
        />
      </span>
      <span className="xl:hidden">
        <IconButton
          icon="sidebar-simple"
          variant="ghost"
          size="sm"
          onClick={onOpenDrawer}
          aria-label={t('card.side.openDrawer')}
          data-testid="side-drawer-open"
        />
      </span>
    </>
  );
}

/**
 * O corpo da aba ativa. Na conversa é sempre um grid: uma coluna só, que ganha a
 * segunda (a viagem) a partir de xl quando há viagem à mostra — abaixo de xl a
 * viagem só existe no Drawer, aberto pelo header. Nas outras abas é o painel
 * rolável de sempre.
 */
function CardDetailBody({
  tab,
  cardId,
  activeTaskId,
  tripState,
  withSideColumn,
}: Readonly<{
  tab: CardDetailTabId;
  cardId: CardId;
  activeTaskId?: string;
  tripState: CardTripState;
  withSideColumn: boolean;
}>): React.JSX.Element {
  if (tab !== 'conversation')
    return (
      <div className="flex-1 overflow-auto px-5 py-6">
        <CardTabBody tab={tab} cardId={cardId} activeTaskId={activeTaskId} />
      </div>
    );
  // O grid de uma coluna (`main-only` é `minmax(0,1fr)`) existe mesmo sem a
  // coluna lateral: só a célula dela entra e sai. Trocar a raiz do corpo
  // remontaria a conversa (perdendo scroll e estado) toda vez que ela aparecesse.
  return (
    <div
      className={twcn(
        'grid min-h-0 flex-1 grid-cols-main-only',
        withSideColumn && 'xl:grid-cols-conversation-trip',
      )}
    >
      <div className="flex min-h-0 min-w-0 flex-col">
        <ConversationView cardId={cardId} />
      </div>
      {withSideColumn ? (
        <div className="hidden min-h-0 xl:flex">
          <CardSideColumn cardId={cardId} tripState={tripState} variant="column" />
        </div>
      ) : null}
    </div>
  );
}

function CardTabBody({
  tab,
  cardId,
  activeTaskId,
}: Readonly<{
  tab: CardDetailTabId;
  cardId: CardId;
  activeTaskId?: string;
}>): React.JSX.Element {
  if (tab === 'tasks') return <TasksView cardId={cardId} activeTaskId={activeTaskId} />;
  if (tab === 'identity') return <IdentityView cardId={cardId} />;
  if (tab === 'history') return <HistoryView cardId={cardId} />;
  return (
    <EmptyHint testId="drawer-tab-placeholder">
      {t('board.drawer.tabPlaceholder', { tab: t(TAB_LABEL[tab]) })}
    </EmptyHint>
  );
}

function CardDetailTabs({
  routes,
  activeTab,
}: Readonly<{
  routes: CardRoutes;
  activeTab: CardDetailTabId;
}>): React.JSX.Element {
  return (
    <TabBar ariaLabel={t('board.drawer.tabsAria')}>
      {TABS.map((id) => (
        <TabBar.Tab
          key={id}
          {...routes.tab(id)}
          icon={TAB_ICON[id]}
          label={t(TAB_LABEL[id])}
          isActive={id === activeTab}
        />
      ))}
    </TabBar>
  );
}

function CardHeaderIdentity({ detail }: Readonly<{ detail: CardDetailEntity }>): React.JSX.Element {
  const zendeskTicket = detail.externalRefs.find(
    (r) => r.system === 'zendesk' && r.type === 'ticket',
  );
  return (
    <>
      <CustomerAvatar name={detail.customer.name} vip={detail.customer.vip} size="md" />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 min-w-0">
          <span className="truncate text-base font-medium text-foreground">
            {detail.customer.name}
          </span>
          <span className="font-mono text-tiny uppercase tracking-eyebrow text-faint-foreground">
            #{detail.seq}
          </span>
        </div>
        {detail.subject ? (
          <span className="truncate text-card-title leading-card-title text-foreground">
            {detail.subject}
          </span>
        ) : null}
        {detail.summary ? (
          <p className="m-0 line-clamp-2 text-meta leading-snug text-muted-foreground">
            {detail.summary}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-1.5 text-meta text-muted-foreground">
          <span className="truncate">{detail.company.name}</span>
          <span className="text-faint-foreground">·</span>
          <ChannelChip channel={detail.originChannel} />
          {zendeskTicket ? (
            <a
              href={`${env.VITE_ZENDESK_URL}/agent/tickets/${zendeskTicket.externalId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ToneChip tone="gray" icon="link-simple">
                {t('card.zendeskTicket', { id: zendeskTicket.externalId })}
              </ToneChip>
            </a>
          ) : null}
        </div>
      </div>
    </>
  );
}

/**
 * Header badge naming the agent currently acting on the card. Shows the
 * assignee's avatar + name + kind badge (human / AI), or a muted empty state
 * when the card is unassigned. `agentDisplayName` is resolved server-side at
 * read time; when absent we treat the card as unassigned.
 */
function CardHeaderAgent({ detail }: Readonly<{ detail: CardDetailEntity }>): React.JSX.Element {
  const actor =
    detail.assigneeAgent && detail.agentDisplayName
      ? { kind: detail.assigneeAgent.kind, name: detail.agentDisplayName }
      : null;

  return (
    <div className="flex shrink-0 items-center gap-2" data-testid="card-assignee">
      {actor ? (
        <>
          <Avatar size="md" tone={actor.kind === 'agent' ? 'ai' : 'neutral'}>
            <AvatarFallback>
              <AppIcons name={agentKindIcon(actor.kind)} size={16} />
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-base font-medium text-foreground">
              {t('board.drawer.subheader.assigned')}
            </span>
            <span className="truncate text-meta text-muted-foreground">{actor.name}</span>
          </div>
        </>
      ) : (
        <span className="text-meta text-muted-foreground">{t('board.drawer.subheader.none')}</span>
      )}
    </div>
  );
}

function CardHeaderIdentitySkeleton({
  cardId,
  errored,
}: Readonly<{
  cardId: CardId;
  errored: boolean;
}>): React.JSX.Element {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <span className="font-mono text-tiny uppercase tracking-eyebrow-wide text-faint-foreground">
        {t('board.drawer.cardEyebrow')}
      </span>
      <span className="font-mono text-meta text-foreground">{cardId}</span>
      <span className="flex-1" />
      {errored ? (
        <span className="text-meta text-tone-red-fg">{t('board.drawer.errorLoading')}</span>
      ) : null}
    </div>
  );
}

function isCardDetailTab(value: unknown): value is CardDetailTabId {
  return typeof value === 'string' && (TABS as readonly string[]).includes(value);
}
